import { create } from 'zustand';
import authFetch from '../utils/authFetch';
import useBoardStore from './boardStore';

function shouldShowBrowser() {
  return typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted';
}

let _audioCtx = null;
function playBeep() {
  try {
    if (typeof window === 'undefined') return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!_audioCtx) _audioCtx = new AC();
    if (_audioCtx.state === 'suspended') _audioCtx.resume();
    const now = _audioCtx.currentTime;
    const o1 = _audioCtx.createOscillator();
    const g1 = _audioCtx.createGain();
    o1.type = 'sine';
    o1.frequency.setValueAtTime(880, now);
    g1.gain.setValueAtTime(0.0001, now);
    g1.gain.exponentialRampToValueAtTime(0.28, now + 0.02);
    g1.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    o1.connect(g1).connect(_audioCtx.destination);
    o1.start(now);
    o1.stop(now + 0.24);
    const o2 = _audioCtx.createOscillator();
    const g2 = _audioCtx.createGain();
    o2.type = 'sine';
    o2.frequency.setValueAtTime(1175, now + 0.12);
    g2.gain.setValueAtTime(0.0001, now + 0.12);
    g2.gain.exponentialRampToValueAtTime(0.28, now + 0.14);
    g2.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
    o2.connect(g2).connect(_audioCtx.destination);
    o2.start(now + 0.12);
    o2.stop(now + 0.36);
  } catch {}
}

function showBrowserNotification(n) {
  if (!shouldShowBrowser()) return;
  try {
    const notif = new Notification(n.title, {
      body: n.body || '',
      tag: `seasymon-${n.id}`,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      requireInteraction: false,
      silent: false,
    });
    notif.onclick = () => {
      try { window.focus(); } catch {}
      notif.close();
    };
  } catch (err) {
    console.warn('Notification failed:', err);
  }
}

async function reloadSidebar() {
  try {
    const store = useBoardStore.getState();
    await store.fetchWorkspaces();
    const wsList = useBoardStore.getState().workspaces || [];
    await Promise.all(wsList.map((w) => store.fetchBoards(w.id)));
  } catch (err) {
    console.error('reloadSidebar failed:', err);
  }
}

const useNotificationStore = create((set, get) => ({
  list: [],
  unread: 0,
  _pollTimer: null,
  _seenIds: new Set(),

  fetchList: async () => {
    try {
      const list = await authFetch('/api/v1/notifications');
      const unread = list.filter((n) => !n.read_at).length;
      const seen = get()._seenIds;
      const fresh = list.filter((n) => !n.read_at && !seen.has(n.id));
      if (seen.size > 0 && fresh.length > 0) {
        playBeep();
        for (const n of fresh) showBrowserNotification(n);
      }
      if (seen.size > 0 && fresh.some((n) => n.type === 'board_added' || n.type === 'board_removed')) {
        reloadSidebar();
      }
      const newSeen = new Set(seen);
      list.forEach((n) => newSeen.add(n.id));
      set({ list, unread, _seenIds: newSeen });
    } catch (err) {
      console.error('fetchNotifications failed:', err);
    }
  },

  markRead: async (id) => {
    set((s) => ({
      list: s.list.map((n) => (n.id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n)),
      unread: Math.max(0, s.unread - (get().list.find((n) => n.id === id && !n.read_at) ? 1 : 0)),
    }));
    try {
      await authFetch(`/api/v1/notifications/${id}/read`, { method: 'POST' });
    } catch {}
  },

  markAllRead: async () => {
    set((s) => ({
      list: s.list.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() })),
      unread: 0,
    }));
    try {
      await authFetch('/api/v1/notifications/read-all', { method: 'POST' });
    } catch {}
  },

  startPolling: (intervalMs = 15000) => {
    get().stopPolling();
    get().fetchList();
    const timer = setInterval(() => get().fetchList(), intervalMs);
    set({ _pollTimer: timer });
  },

  stopPolling: () => {
    const { _pollTimer } = get();
    if (_pollTimer) { clearInterval(_pollTimer); set({ _pollTimer: null }); }
  },

  requestBrowserPermission: async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    if (Notification.permission === 'granted' || Notification.permission === 'denied') return Notification.permission;
    try { return await Notification.requestPermission(); } catch { return 'default'; }
  },
}));

export default useNotificationStore;
